import * as THREE from 'three';
import { global } from '../../core/GlobalState';
const RADIAL_STIFFNESS = 6.2;
const MAX_APPROACH_VELOCITY = 12000;
function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
function quadraticBezier(a, b, c, t, target) {
    const oneMinusT = 1 - t;
    target.set(0, 0, 0);
    target.addScaledVector(a, oneMinusT * oneMinusT);
    target.addScaledVector(b, 2 * oneMinusT * t);
    target.addScaledVector(c, t * t);
    return target;
}
function describeOrb(orb) {
    return orb.labelText || orb.orbType || 'orb';
}
export class PlayerOrbitController {
    constructor() {
        this.state = null;
        this.tmpVec1 = new THREE.Vector3();
        this.tmpVec2 = new THREE.Vector3();
        this.tmpVec3 = new THREE.Vector3();
    }
    begin(options) {
        var _a;
        const start = global.controls.getObject().position.clone();
        const direction = options.direction && Math.abs(options.direction) > 0 ? Math.sign(options.direction) : 1;
        const planeNormal = options.planeNormal.clone().normalize();
        this.state = {
            phase: 'approach',
            orb: options.orb,
            entryPoint: options.entryPoint.clone(),
            controlPoint: options.controlPoint.clone(),
            startPoint: start,
            planeNormal,
            orbitRadius: options.orbitRadius,
            orbitSpeed: options.orbitSpeed,
            direction,
            approachDuration: Math.max(0.12, options.approachDuration),
            elapsed: 0,
            unlockOnExit: (_a = options.unlockOnExit) !== null && _a !== void 0 ? _a : false,
            announcedCapture: false,
        };
        global.orbitingTarget = options.orb;
    }
    isActive() {
        return this.state !== null;
    }
    cancel(options = {}) {
        var _a, _b, _c, _d;
        const prevState = this.state;
        this.state = null;
        const wasOrbiting = !!global.orbitingTarget || !!prevState;
        global.orbitingTarget = null;
        if (!prevState && !wasOrbiting) {
            return;
        }
        const shouldUnlock = (_b = (_a = options.unlockCamera) !== null && _a !== void 0 ? _a : prevState === null || prevState === void 0 ? void 0 : prevState.unlockOnExit) !== null && _b !== void 0 ? _b : false;
        if (options.announce) {
            const message = (_c = options.message) !== null && _c !== void 0 ? _c : '🛰️ Orbit disengaged.';
            global.chat.addMessage(message);
        }
        if (shouldUnlock && typeof ((_d = global.controls) === null || _d === void 0 ? void 0 : _d.unlock) === 'function' && global.controls.isLocked) {
            try {
                global.controls.unlock();
            }
            catch (error) {
                console.warn('[PlayerOrbitController] Failed to unlock controls', error);
            }
        }
    }
    update(context) {
        if (!this.state)
            return;
        const { velocity, controls, camera, deltaSeconds } = context;
        const state = this.state;
        const playerObject = controls.getObject();
        const orbGroup = state.orb.group;
        if (!orbGroup) {
            this.cancel({ announce: true, message: '⚠️ Orbit target lost.', unlockCamera: state.unlockOnExit });
            return;
        }
        const orbPosition = orbGroup.position;
        const currentPosition = playerObject.position;
        if (state.phase === 'approach') {
            state.elapsed += deltaSeconds;
            const progress = Math.min(1, state.elapsed / state.approachDuration);
            const eased = easeInOutCubic(progress);
            const targetPos = quadraticBezier(state.startPoint, state.controlPoint, state.entryPoint, eased, this.tmpVec1);
            const worldDelta = this.tmpVec2.copy(targetPos).sub(currentPosition);
            const desiredVelocity = worldDelta.multiplyScalar(1 / Math.max(deltaSeconds, 1e-4));
            if (desiredVelocity.length() > MAX_APPROACH_VELOCITY) {
                desiredVelocity.setLength(MAX_APPROACH_VELOCITY);
            }
            this.applyVelocity(desiredVelocity, velocity, camera);
            if (progress >= 1) {
                // Snap to entry point to avoid drift and transition to orbit
                currentPosition.copy(state.entryPoint);
                velocity.set(0, 0, 0);
                state.phase = 'orbit';
                state.announcedCapture = false;
            }
            return;
        }
        // Maintain orbit
        const offset = this.tmpVec1.copy(currentPosition).sub(orbPosition);
        if (offset.lengthSq() < 1e-4) {
            offset.copy(state.entryPoint).sub(orbPosition);
            if (offset.lengthSq() < 1e-4) {
                offset.set(1, 0, 0);
            }
        }
        const radialDir = offset.clone().normalize();
        const tangent = this.tmpVec2.copy(state.planeNormal).cross(radialDir).normalize().multiplyScalar(state.direction);
        const tangentialVelocity = tangent.multiplyScalar(state.orbitSpeed);
        const radiusError = offset.length() - state.orbitRadius;
        const radialCorrection = radialDir.multiplyScalar(-radiusError * RADIAL_STIFFNESS);
        const desiredVelocity = tangentialVelocity.add(radialCorrection);
        this.applyVelocity(desiredVelocity, velocity, camera);
        if (!state.announcedCapture) {
            state.announcedCapture = true;
            global.chat.addMessage(`🛰️ Stable orbit around ${describeOrb(state.orb)}.`);
        }
    }
    applyVelocity(desiredWorldVelocity, velocity, camera) {
        const forward = this.tmpVec1;
        const right = this.tmpVec2;
        const up = this.tmpVec3;
        camera.getWorldDirection(forward).normalize();
        up.copy(camera.up).normalize();
        if (up.lengthSq() === 0) {
            up.set(0, 1, 0);
        }
        right.crossVectors(forward, up).normalize();
        up.crossVectors(right, forward).normalize();
        const rightComponent = desiredWorldVelocity.dot(right);
        const forwardComponent = desiredWorldVelocity.dot(forward);
        const upComponent = desiredWorldVelocity.dot(up);
        velocity.set(-rightComponent, upComponent, -forwardComponent);
    }
}
export const playerOrbitController = new PlayerOrbitController();
